/**
 * UserForm.js
 * Component for creating and editing users/athletes
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Tab,
  Tabs
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { userService, authService, medicalCertificateService, roleService, settingsService, portalService, parentService } from '../../services/api';
import { USER_ROLES, CERTIFICATE_TYPE_LABELS, SYSTEM_ROLE_LABELS } from '../../utils/constants';
import { isMinor, isMember } from '../../utils/userHelpers';
import Select from 'react-select';
import UserMedicalCertificates from './UserMedicalCertificates';
import UserDocuments from './UserDocuments';
import UserMemberships from './UserMemberships';
import ParentSection from './ParentSection';
import UserGroups from './UserGroups';
import UserRentals from './UserRentals';
import SendBroadcastEmailModal from './SendBroadcastEmailModal';
import UserPortalAccess from './UserPortalAccess';
import SendAccessLinkModal from './SendAccessLinkModal';
import EmailWarningModal from '../Common/EmailWarningModal';
import { checkEmailsForWarnings } from '../../utils/emailValidation';

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const isCurrentUserAdmin = authService.hasRole(USER_ROLES.ADMIN);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    phoneNumber: '',
    address: '',
    fiscalCode: '',
    roles: [USER_ROLES.ATHLETE],
    birthMunicipality: '',
    residenceMunicipality: '',
    residenceProvince: '',
    residencePostalCode: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [userData, setUserData] = useState(null);
  const [parentData, setParentData] = useState(null);
  const [expiredCertificates, setExpiredCertificates] = useState([]);
  const [showExpiredDetails, setShowExpiredDetails] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAccessLinkModal, setShowAccessLinkModal] = useState(false);
  const [emailWarnings, setEmailWarnings] = useState([]);
  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState(() =>
    Object.entries(SYSTEM_ROLE_LABELS).map(([value, label]) => ({ value, label }))
  );
  const [downloadPdfLoading, setDownloadPdfLoading] = useState(false);
  const [sendPdfLoading, setSendPdfLoading] = useState(false);
  const [portalSections, setPortalSections] = useState([]);
  const [portalAttachments, setPortalAttachments] = useState([]);
  const [userPortalConsents, setUserPortalConsents] = useState([]);
  const [portalConsentsLoading, setPortalConsentsLoading] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // Funzione per caricare i certificati medici e controllare se ce ne sono di scaduti
  const loadMedicalCertificates = useCallback(async (userId) => {
    try {
      const response = await medicalCertificateService.getUserCertificates(
        userId,
        'all',
        1,
        100, // Carica tutti i certificati
        false,
        'active' // Solo certificati attivi (non archiviati)
      );

      if (response.success && response.data) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const activeCerts = response.data.filter(cert => !cert.isArchived);

        // Controlla se ci sono certificati validi salvati (non archiviati)
        const hasValidCertificates = activeCerts.some(cert => {
          if (!cert.expiryDate) return false;
          const expiryDate = new Date(cert.expiryDate);
          expiryDate.setHours(0, 0, 0, 0);
          return expiryDate >= today;
        });

        // Mostra l'alert solo se ci sono certificati scaduti e NESSUN certificato valido salvato
        if (hasValidCertificates) {
          setExpiredCertificates([]);
        } else {
          // Filtra i certificati scaduti
          const expired = activeCerts.filter(cert => {
            if (!cert.expiryDate) return false;
            const expiryDate = new Date(cert.expiryDate);
            expiryDate.setHours(0, 0, 0, 0);
            return expiryDate < today;
          });

          setExpiredCertificates(expired);
        }
      } else {
        setExpiredCertificates([]);
      }
    } catch (err) {
      console.error('Error loading medical certificates:', err);
      // Non mostriamo errore all'utente, è solo un controllo supplementare
    }
  }, []);

  const loadAvailableRoles = useCallback(async () => {
    try {
      const result = await roleService.getAppRoles();
      const baseRoles = result?.success && Array.isArray(result.data)
        ? result.data.map(role => ({
            value: role.name,
            label: role.label || role.name
          }))
        : [];

      const roleMap = new Map();
      Object.entries(SYSTEM_ROLE_LABELS).forEach(([val, lbl]) => {
        roleMap.set(val, { value: val, label: lbl });
      });
      baseRoles.forEach(r => {
        roleMap.set(r.value, r);
      });

      setAvailableRoles(Array.from(roleMap.values()));
    } catch (err) {
      console.error('Error loading roles:', err);
      setAvailableRoles(
        Object.entries(SYSTEM_ROLE_LABELS).map(([value, label]) => ({
          value,
          label
        }))
      );
    }
  }, []);

  const loadPortalData = useCallback(async () => {
    try {
      setPortalConsentsLoading(true);
      const [settingsRes, attachmentsRes, userConsentsRes, sectionsRes] = await Promise.all([
        settingsService.getSettings().catch(() => ({ success: false, data: [] })),
        portalService.getAttachments().catch(() => ({ success: false, data: [] })),
        id ? portalService.getUserConsents(id).catch(() => ({ success: false, data: [] })) : Promise.resolve({ success: false, data: [] }),
        typeof portalService.getSections === 'function'
          ? portalService.getSections().catch(() => ({ success: false, data: [] }))
          : Promise.resolve({ success: false, data: [] })
      ]);

      let loadedSections = [];
      if (settingsRes.success && Array.isArray(settingsRes.data)) {
        const sectionsSetting = settingsRes.data.find(s => s.key === 'Portal.Sections');
        if (sectionsSetting && sectionsSetting.value) {
          try {
            const parsed = JSON.parse(sectionsSetting.value);
            if (Array.isArray(parsed) && parsed.length > 0) {
              loadedSections = parsed;
            }
          } catch (e) {
            console.error('Error parsing Portal.Sections JSON in UserForm:', e);
          }
        }
      }

      if (loadedSections.length === 0 && sectionsRes?.success && Array.isArray(sectionsRes.data)) {
        loadedSections = sectionsRes.data;
      }

      if (loadedSections.length > 0) {
        setPortalSections(loadedSections);
      }

      if (attachmentsRes.success && Array.isArray(attachmentsRes.data)) {
        setPortalAttachments(attachmentsRes.data);
      }

      if (userConsentsRes && userConsentsRes.success && Array.isArray(userConsentsRes.data)) {
        setUserPortalConsents(userConsentsRes.data.filter(c => c.isEnabled !== false));
      }
    } catch (err) {
      console.error('Error loading portal data in UserForm:', err);
    } finally {
      setPortalConsentsLoading(false);
    }
  }, [id]);

  const getAttachmentFileIcon = (fileName) => {
    if (!fileName) return 'fas fa-file text-secondary';
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'fas fa-file-pdf text-danger';
    if (ext === 'doc' || ext === 'docx') return 'fas fa-file-word text-primary';
    if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return 'fas fa-file-image text-success';
    return 'fas fa-file text-secondary';
  };

  const formatAttachmentSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    if (mb < 1) {
      const kb = bytes / 1024;
      return `${kb.toFixed(1)} KB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const handleDownloadAttachmentFile = async (attachment) => {
    try {
      setDownloadingAttachmentId(attachment.id);
      const res = await portalService.downloadAttachment(attachment);
      if (!res.success) {
        setError(res.message || 'Errore durante il download del file');
      }
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setError('Errore durante il download del file');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setParentData(null);
      const response = await userService.getUser(id);

      if (response.success) {
        const user = response.data;
        setUserData(user); // Save user data for certificates component
        setFormData({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
          phoneNumber: user.phoneNumber || '',
          address: user.address || '',
          fiscalCode: user.fiscalCode || '',
          roles: user.roles?.length > 0 ? user.roles : [USER_ROLES.ATHLETE],
          birthMunicipality: user.birthMunicipality || '',
          residenceMunicipality: user.residenceMunicipality || '',
          residenceProvince: user.residenceProvince || '',
          residencePostalCode: user.residencePostalCode || ''
        });

        if (user.parent) {
          setParentData(user.parent);
        }

        // Carica i certificati medici per controllare se ce ne sono di scaduti
        await loadMedicalCertificates(id);
      } else {
        setError('Utente non trovato');
      }
    } catch (err) {
      setError('Errore nel caricamento dell\'utente');
      console.error('Error loading user:', err);
    } finally {
      setLoading(false);
    }
  }, [id, loadMedicalCertificates]);

  // Load user data for editing
  useEffect(() => {
    loadAvailableRoles();
    if (isEditMode) {
      loadUser();
      loadPortalData();
    }
  }, [isEditMode, loadUser, loadAvailableRoles, loadPortalData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.firstName?.trim()) {
      setError('Il nome è obbligatorio');
      return false;
    }
    if (!formData.lastName?.trim()) {
      setError('Il cognome è obbligatorio');
      return false;
    }
    if (!Array.isArray(formData.roles) || formData.roles.length === 0) {
      setError('Seleziona almeno un ruolo');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e, bypassEmailWarning = false) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    if (!bypassEmailWarning) {
      const isAthleteMinor = isMinor(formData.dateOfBirth, formData.fiscalCode);
      const fieldsToCheck = [
        {
          label: isAthleteMinor ? 'Email Atleta' : 'Email',
          value: formData.email,
          fieldId: 'user-email',
          selector: 'input[name="email"]',
        },
        {
          label: 'Email Genitore',
          value: parentData?.email,
          fieldId: 'parent-email',
        },
      ];
      const warnings = checkEmailsForWarnings(fieldsToCheck);
      if (warnings.length > 0) {
        setEmailWarnings(warnings);
        setShowEmailWarningModal(true);
        return;
      }
    }

    try {
      setSubmitLoading(true);
      setError('');
      setSuccess('');

      // Prepare data for submission
      const submitData = { ...formData };
      if (isMinor(formData.dateOfBirth, formData.fiscalCode)) {
        const parentEmail = parentData?.email?.trim() || parentData?.parentUser?.loginEmail?.trim() || parentData?.parentUser?.email?.trim();
        if (!submitData.email?.trim() && parentEmail) {
          submitData.email = parentEmail;
        }
      }

      submitData.email = submitData.email?.trim() || null;
      submitData.phoneNumber = submitData.phoneNumber?.trim() || null;
      submitData.fiscalCode = submitData.fiscalCode?.trim() || null;
      submitData.dateOfBirth = submitData.dateOfBirth || null;
      submitData.birthMunicipality = submitData.birthMunicipality?.trim() || null;
      submitData.address = submitData.address?.trim() || null;
      submitData.residenceMunicipality = submitData.residenceMunicipality?.trim() || null;
      submitData.residenceProvince = submitData.residenceProvince?.trim() || null;
      submitData.residencePostalCode = submitData.residencePostalCode?.trim() || null;

      let response;
      if (isEditMode) {
        response = await userService.updateUser(id, submitData);
      } else {
        response = await userService.createUser(submitData);
      }

      if (response.success) {
        const targetUserId = isEditMode ? id : (response.data?.id || response.data);
        if (targetUserId && parentData && !parentData.id) {
          const hasParentData = Object.entries(parentData).some(
            ([k, v]) => k !== 'id' && v && typeof v === 'string' && v.trim() !== ''
          );
          if (hasParentData) {
            try {
              await parentService.createParent({ ...parentData, userId: targetUserId });
            } catch (parentErr) {
              console.error('Error saving parent data on user save:', parentErr);
            }
          }
        }

        setSuccess(
          isEditMode
            ? 'Utente aggiornato con successo'
            : 'Utente creato con successo'
        );

        // Redirect after successful submission
        setTimeout(() => {
          navigate('/users');
        }, 2000);
      } else {
        setError(response.message || 'Errore nel salvataggio dell\'utente');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error saving user:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDownloadRegistrationForm = async () => {
    try {
      setDownloadPdfLoading(true);
      setError('');
      const fileName = userData ? `modulo-iscrizione-${userData.lastName}-${userData.firstName}.pdf` : undefined;
      const result = await userService.downloadRegistrationForm(id, fileName);
      if (!result.success) {
        setError(result.message || 'Errore durante il download del modulo di iscrizione');
      }
    } finally {
      setDownloadPdfLoading(false);
    }
  };

  const handleSendRegistrationForm = async () => {
    try {
      setSendPdfLoading(true);
      setError('');
      setSuccess('');
      const result = await userService.sendRegistrationForm(id);
      if (result.success) {
        setSuccess(`Modulo di iscrizione inviato a ${result.data.recipientEmail}`);
      } else {
        setError(result.message || 'Errore durante l\'invio del modulo di iscrizione');
      }
    } finally {
      setSendPdfLoading(false);
    }
  };

  const handleToggleArchive = async () => {
    if (!userData) return;
    const isArchiving = !userData.isArchived;
    const confirmMessage = isArchiving
      ? `Sei sicuro di voler archiviare l'atleta ${userData.firstName} ${userData.lastName}? Verrà nascosto dalle liste attive.`
      : `Sei sicuro di voler ripristinare l'atleta ${userData.firstName} ${userData.lastName}? Tornerà visibile nelle liste attive.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setArchiveLoading(true);
      setError('');
      setSuccess('');
      const response = await userService.archiveUser(userData.id, isArchiving);
      if (response.success) {
        setSuccess(response.message || (isArchiving ? 'Atleta archiviato con successo' : 'Atleta ripristinato con successo'));
        if (isArchiving) {
          setTimeout(() => {
            navigate('/users');
          }, 1500);
        } else {
          await loadUser();
        }
      } else {
        setError(response.message || `Errore durante ${isArchiving ? "l'archiviazione" : 'il ripristino'} dell'atleta`);
      }
    } catch (err) {
      console.error('Error toggling archive status:', err);
      setError('Errore di connessione al server');
    } finally {
      setArchiveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="user-form fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {isEditMode && userData
            ? `${userData.firstName} ${userData.lastName}`
            : isEditMode
              ? 'Modifica Utente'
              : 'Nuovo Utente'
          }
        </h2>
        <Button variant="outline-secondary" onClick={() => navigate('/users')}>
          <i className="fas fa-arrow-left me-2"></i>
          Torna alla Lista
        </Button>
      </div>

      {/* Alerts */}
      {userData?.isArchived && isEditMode && (
        <Alert variant="warning" className="mb-3 d-flex align-items-center">
          <i className="fas fa-archive fa-lg me-2"></i>
          <div>
            <strong>Atleta Archiviato:</strong> Questo atleta è attualmente archiviato e nascosto dagli elenchi attivi del gestionale.
          </div>
        </Alert>
      )}
      {expiredCertificates.length > 0 && isEditMode && (
        <Alert variant="warning" className="mb-3">
          <div 
            onClick={() => setShowExpiredDetails(!showExpiredDetails)}
            style={{ cursor: 'pointer' }}
          >
            <div className="mb-0 d-flex justify-content-between align-items-center">
              <strong>
                <i className="fas fa-exclamation-triangle me-2"></i>
                Certificati Medici Scaduti
              </strong>
              <i className={`fas fa-chevron-${showExpiredDetails ? 'up' : 'down'}`}></i>
            </div>
            <p className="mb-0 mt-2">
              {expiredCertificates.length === 1
                ? 'L\'atleta ha 1 certificato medico scaduto.'
                : `L'atleta ha ${expiredCertificates.length} certificati medici scaduti.`}
              {' '}
              <small className="text-muted">Clicca per {showExpiredDetails ? 'nascondere' : 'visualizzare'} i dettagli</small>
            </p>
          </div>
          
          {showExpiredDetails && (
            <>
              <hr />
              <ul className="mb-3">
                {expiredCertificates.map(cert => (
                  <li key={cert.id}>
                    {CERTIFICATE_TYPE_LABELS[cert.certificateType] || cert.certificateType}
                    {cert.certificateNumber && ` - N. ${cert.certificateNumber}`}
                    {' - Scaduto il: '}
                    {new Date(cert.expiryDate).toLocaleDateString('it-IT')}
                  </li>
                ))}
              </ul>
              <div className="mb-0">
                <Button
                  variant="warning"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab('certificates');
                  }}
                >
                  <i className="fas fa-file-medical me-2"></i>
                  Vai ai Certificati Medici
                </Button>
              </div>
            </>
          )}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Tabs
            id="user-form-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
          >
            {/* Tab Dati Personali */}
            <Tab eventKey="personal" title={
              <span>
                <i className="fas fa-user me-2"></i>
                Dati Personali
              </span>
            }>
              <div className="mb-4">
                <ParentSection
                  key={userData?.id || id}
                  userId={userData?.id}
                  parentData={parentData || userData?.parent}
                  onParentChange={(data) => setParentData(data)}
                  defaultOpen={isMinor(formData.dateOfBirth, formData.fiscalCode) && !(parentData?.id || userData?.parent?.id)}
                  allowUserSearch={true}
                />
              </div>

              {userData?.childrenParentRecords && userData.childrenParentRecords.length > 0 && (
                <Alert variant="info" className="mb-4 d-flex align-items-start" data-testid="linked-children-box">
                  <i className="fas fa-child fs-4 me-3 text-info mt-1"></i>
                  <div>
                    <h6 className="alert-heading mb-1 fw-bold">Figli atleti collegati come genitore/tutore:</h6>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {userData.childrenParentRecords.map((cp, idx) => (
                        cp.user ? (
                          <button
                            key={cp.id || idx}
                            type="button"
                            className="btn btn-light bg-white text-dark border px-3 py-2 fs-6 fw-bold d-inline-flex align-items-center shadow-sm"
                            onClick={() => {
                              navigate(`/users/${cp.user.id}/edit`);
                              if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                                window.scrollTo(0, 0);
                              }
                            }}
                            title={`Vai alla scheda di ${cp.user.firstName} ${cp.user.lastName}`}
                            data-testid={`child-athlete-btn-${cp.user.id}`}
                          >
                            <i className="fas fa-user me-2 text-primary"></i>
                            <span>{cp.user.firstName} {cp.user.lastName}</span>
                          </button>
                        ) : null
                      ))}
                    </div>
                  </div>
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                {/* Personal Information */}
                <div className="form-section">
                  <h5>Informazioni Personali Atleta</h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="user-firstName">
                        <Form.Label>Nome *</Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Inserisci il nome"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="user-lastName">
                        <Form.Label>Cognome *</Form.Label>
                        <Form.Control
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Inserisci il cognome"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="user-dateOfBirth">
                        <Form.Label>Data di Nascita</Form.Label>
                        <Form.Control
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="user-birthMunicipality">
                        <Form.Label>Luogo di Nascita</Form.Label>
                        <Form.Control
                          type="text"
                          name="birthMunicipality"
                          value={formData.birthMunicipality}
                          onChange={handleInputChange}
                          placeholder="Milano"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3" controlId="user-fiscalCode">
                        <Form.Label>Codice Fiscale</Form.Label>
                        <Form.Control
                          type="text"
                          name="fiscalCode"
                          value={formData.fiscalCode}
                          onChange={handleInputChange}
                          placeholder="RSSMRA90A01H501X"
                          maxLength={16}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="user-address">
                    <Form.Label>Indirizzo</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Via Roma 123"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="user-residenceMunicipality">
                        <Form.Label>Comune</Form.Label>
                        <Form.Control
                          type="text"
                          name="residenceMunicipality"
                          value={formData.residenceMunicipality}
                          onChange={handleInputChange}
                          placeholder="Milano"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="user-residenceProvince">
                        <Form.Label>Provincia</Form.Label>
                        <Form.Control
                          type="text"
                          name="residenceProvince"
                          value={formData.residenceProvince}
                          onChange={handleInputChange}
                          placeholder="MI"
                          maxLength={2}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="user-residencePostalCode">
                        <Form.Label>CAP</Form.Label>
                        <Form.Control
                          type="text"
                          name="residencePostalCode"
                          value={formData.residencePostalCode}
                          onChange={handleInputChange}
                          placeholder="20100"
                          maxLength={5}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {(() => {
                    const isAthleteMinor = isMinor(formData.dateOfBirth, formData.fiscalCode);
                    const parentEmail = parentData?.email?.trim() || parentData?.parentUser?.loginEmail?.trim() || parentData?.parentUser?.email?.trim();
                    const parentPhone = parentData?.phoneNumber?.trim() || parentData?.parentUser?.phoneNumber?.trim();

                    const showEmail = !isAthleteMinor || !parentEmail;
                    const showPhone = !isAthleteMinor || !parentPhone;

                    if (!showEmail && !showPhone) {
                      return (
                        <Alert variant="light" className="border d-flex align-items-center mb-3 py-2 px-3 text-muted" data-testid="parent-contacts-summary">
                          <i className="fas fa-user-friends me-2 text-primary"></i>
                          <div className="small">
                            <strong>Recapiti genitore/tutore attivi:</strong> Email: <span className="text-dark fw-medium">{parentEmail}</span> | Telefono: <span className="text-dark fw-medium">{parentPhone}</span>
                            <span className="ms-2">(gestiti nella sezione Dati Genitore sopra)</span>
                          </div>
                        </Alert>
                      );
                    }

                    return (
                      <Row>
                        {showEmail && (
                          <Col md={showPhone ? 6 : 12}>
                            <Form.Group className="mb-3" controlId="user-email">
                              <Form.Label>{isAthleteMinor ? 'Email Atleta' : 'Email'}</Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="email@esempio.com"
                              />
                              {isAthleteMinor && (
                                <Form.Text className="text-muted d-block">
                                  Recapito alternativo dell'atleta (email genitore non presente). Per inserire l'email del genitore, usa la sezione <strong>Dati Genitore</strong> sopra.
                                </Form.Text>
                              )}
                              {isEditMode && userData && formData.email && (
                                <button
                                  type="button"
                                  className="btn btn-link p-0 mt-1 text-primary small d-inline-flex align-items-center"
                                  onClick={() => setShowEmailModal(true)}
                                >
                                  <i className="fas fa-envelope me-1"></i>
                                  Scrivi email
                                </button>
                              )}
                            </Form.Group>
                          </Col>
                        )}
                        {showPhone && (
                          <Col md={showEmail ? 6 : 12}>
                            <Form.Group className="mb-3" controlId="user-phoneNumber">
                              <Form.Label>{isAthleteMinor ? 'Telefono Atleta' : 'Telefono'}</Form.Label>
                              <Form.Control
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                placeholder="+39 339 1234567"
                              />
                              {isAthleteMinor && (
                                <Form.Text className="text-muted d-block">
                                  Recapito alternativo dell'atleta (telefono genitore non presente). Per inserire il telefono del genitore, usa la sezione <strong>Dati Genitore</strong> sopra.
                                </Form.Text>
                              )}
                              {formData.phoneNumber && (
                                <a
                                  href={`https://wa.me/${formData.phoneNumber.replace(/[^\d+]/g, '').replace(/^\+/, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="d-inline-flex align-items-center mt-1 text-success small"
                                >
                                  <i className="fab fa-whatsapp me-1"></i>
                                  Scrivi Whatsapp
                                </a>
                              )}
                            </Form.Group>
                          </Col>
                        )}
                      </Row>
                    );
                  })()}
                </div>

                {/* Account Information */}
                <div className="form-section">
                  <h5>Informazioni Account</h5>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Ruoli</Form.Label>
                        <Select
                          isMulti
                          options={availableRoles.filter(x => isCurrentUserAdmin || x.value !== USER_ROLES.ADMIN)}
                          value={availableRoles.filter(opt => (formData.roles || []).includes(opt.value))}
                          onChange={(selected) => setFormData(prev => ({
                            ...prev,
                            roles: selected ? selected.map(s => s.value) : []
                          }))}
                          classNamePrefix="react-select"
                          placeholder="Seleziona ruoli..."
                        />
                      </Form.Group>
                    </Col>
                    {isEditMode && userData && userData.createdAt && (
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Registrato il</Form.Label>
                          <Form.Control
                            type="text"
                            value={new Date(userData.createdAt).toLocaleString('it-IT', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </div>

                {/* Form Actions */}
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    {isEditMode && userData && (
                      <>
                        <Button
                          variant="outline-primary"
                          className="me-2"
                          onClick={handleDownloadRegistrationForm}
                          disabled={downloadPdfLoading}
                        >
                          {downloadPdfLoading ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="fas fa-file-pdf me-2"></i>
                          )}
                          Scarica Modulo Iscrizione
                        </Button>
                        {isCurrentUserAdmin && (
                          <>
                            <Button
                              variant="outline-secondary"
                              className="me-2"
                              onClick={handleSendRegistrationForm}
                              disabled={sendPdfLoading}
                            >
                              {sendPdfLoading ? (
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              ) : (
                                <i className="fas fa-paper-plane me-2"></i>
                              )}
                              Invia Modulo Iscrizione via Email
                            </Button>
                            <Button
                              variant="outline-success"
                              className="me-2"
                              onClick={() => setShowAccessLinkModal(true)}
                            >
                              <i className="fas fa-key me-2"></i>
                              Invia Link Accesso
                            </Button>
                            {userData?.isArchived ? (
                              <Button
                                variant="success"
                                onClick={handleToggleArchive}
                                disabled={archiveLoading}
                              >
                                {archiveLoading ? (
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                ) : (
                                  <i className="fas fa-undo me-2"></i>
                                )}
                                Ripristina Atleta
                              </Button>
                            ) : (
                              <Button
                                variant="warning"
                                onClick={handleToggleArchive}
                                disabled={archiveLoading}
                              >
                                {archiveLoading ? (
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                ) : (
                                  <i className="fas fa-archive me-2"></i>
                                )}
                                Archivia Atleta
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <Button
                      variant="outline-secondary"
                      className="me-2"
                      onClick={() => navigate('/users')}
                      disabled={submitLoading}
                    >
                      Annulla
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitLoading}
                    >
                      {submitLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {isEditMode ? 'Aggiornamento...' : 'Creazione...'}
                        </>
                      ) : (
                        <>
                          <i className={`fas ${isEditMode ? 'fa-save' : 'fa-plus'} me-2`}></i>
                          {isEditMode ? 'Aggiorna Utente' : 'Crea Utente'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            </Tab>

            {/* Tab Certificati Medici - Solo in modalità modifica */}
            {isEditMode && userData && (
              <Tab eventKey="certificates" title={
                <span>
                  <i className="fas fa-file-medical me-2"></i>
                  Certificati Medici
                </span>
              }>
                {activeTab === 'certificates' && (
                  <UserMedicalCertificates
                    userId={userData.id}
                    onCertificateChange={() => loadMedicalCertificates(userData.id)}
                  />
                )}
              </Tab>
            )}

            {/* Tab Documenti - Solo in modalità modifica */}
            {isEditMode && userData && (
              <Tab eventKey="documents" title={
                <span>
                  <i className="fas fa-file-alt me-2"></i>
                  Documenti
                </span>
              }>
                {activeTab === 'documents' && (
                  <UserDocuments
                    userId={userData.id}
                  />
                )}
              </Tab>
            )}

            {/* Tab Tesseramenti - Solo in modalità modifica */}
            {isEditMode && userData && (
              <Tab eventKey="memberships" title={
                <span>
                  <i className="fas fa-id-card me-2"></i>
                  Tesseramenti
                </span>
              }>
                {activeTab === 'memberships' && (
                  <UserMemberships
                    userId={userData.id}
                  />
                )}
              </Tab>
            )}

            {/* Tab Gruppi - Solo in modalità modifica */}
            {isEditMode && userData && (
              <Tab eventKey="groups" title={
                <span>
                  <i className="fas fa-users me-2"></i>
                  Gruppi
                </span>
              }>
                {activeTab === 'groups' && (
                  <UserGroups
                    userId={userData.id}
                    user={userData}
                  />
                )}
              </Tab>
            )}

            {/* Tab Noleggi - Solo in modalità modifica */}
            {isEditMode && userData && (
              <Tab eventKey="rentals" title={
                <span>
                  <i className="fas fa-box me-2"></i>
                  Noleggi
                </span>
              }>
                {activeTab === 'rentals' && (
                  <UserRentals
                    userId={userData.id}
                  />
                )}
              </Tab>
            )}

            {/* Tab Consensi - Solo in modalità modifica (Sola Lettura) */}
            {isEditMode && userData && (
              <Tab eventKey="consents" title={
                <span>
                  <i className="fas fa-shield-alt me-2"></i>
                  Consensi
                </span>
              }>
                {activeTab === 'consents' && (
                  <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">
                          <i className="fas fa-shield-alt text-primary me-2"></i>
                          Modulo Consensi e Trattamento Dati
                        </h5>
                        <p className="text-muted small mb-0">
                          Riepilogo dei consensi e delle informative legali espresse dall&apos;atleta sul portale.
                        </p>
                      </div>
                      <span className="badge bg-secondary py-2 px-3">
                        <i className="fas fa-lock me-1"></i>
                        Sola Lettura (Amministrazione)
                      </span>
                    </div>

                    <div className="alert alert-info py-2 small mb-4">
                      <i className="fas fa-info-circle me-2"></i>
                      I consensi e le relative dichiarazioni sono espressi direttamente dall&apos;atleta (o genitore/tutore) in fase di iscrizione o tramite il Portale Atleti e sono consultabili in sola lettura a fini di tracciabilità e conformità legale.
                    </div>

                    {/* Richiesta Ammissione / Rinnovo a Socio */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-secondary mb-2">
                        <i className="fas fa-users me-2 text-primary"></i>
                        Richiesta Ammissione / Rinnovo a Socio
                      </h6>
                      <div className="list-group list-group-flush border rounded">
                        <div className="list-group-item p-3 d-flex justify-content-between align-items-center gap-3 flex-wrap">
                          <div className="flex-grow-1 me-2">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                              <h6 className="fw-bold mb-0">Richiesta Ammissione / Rinnovo a Socio</h6>
                              {isMember(formData.roles) && (
                                <span className="badge bg-success">
                                  <i className="fas fa-check-circle me-1"></i>
                                  Atleta e Socio
                                </span>
                              )}
                            </div>
                            <p className="small text-muted mb-0">
                              {isMember(formData.roles)
                                ? "L'atleta ha espresso la richiesta di ammissione/rinnovo in qualità di Socio dell'Associazione Sportiva Dilettantistica A.S.D. Acrosport Cesena (con diritto di partecipazione e voto e versamento della quota associativa annuale)."
                                : "Nessuna richiesta di ammissione come socio registrata (solo tesseramento atleta)."}
                            </p>
                          </div>
                          <div className="flex-shrink-0 d-flex align-items-center justify-content-end" style={{ minWidth: '180px' }}>
                            <span className={`badge py-2 px-3 text-nowrap ${isMember(formData.roles) ? 'bg-success' : 'bg-secondary'}`}>
                              {isMember(formData.roles) ? (
                                <>
                                  <i className="fas fa-check-circle me-1"></i>
                                  RICHIEDO AMMISSIONE / RINNOVO A SOCIO
                                </>
                              ) : (
                                'Non richiesta (Solo Tesseramento Atleta)'
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 1. Consensi Portale Atleta */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-secondary mb-2">
                        <i className="fas fa-file-contract me-2 text-primary"></i>
                        Consensi Portale Atleta
                      </h6>
                      {portalConsentsLoading ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" variant="primary" />
                          <span className="ms-2 small text-muted">Caricamento consensi...</span>
                        </div>
                      ) : (() => {
                        const visiblePortalConsents = userPortalConsents.filter(c => c.isEnabled !== false);
                        if (visiblePortalConsents.length === 0) {
                          return (
                            <div className="alert alert-light border small text-muted mb-0 py-2">
                              <i className="fas fa-info-circle me-2 text-secondary"></i>
                              Nessun consenso configurato o registrato per questo utente.
                            </div>
                          );
                        }
                        return (
                          <div className="list-group list-group-flush border rounded mb-4">
                            {visiblePortalConsents.map((consent, idx) => (
                              <div key={consent.id} className="list-group-item p-3 d-flex justify-content-between align-items-center gap-3 flex-wrap flex-md-nowrap">
                                <div className="flex-grow-1 me-md-2 min-w-0">
                                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                    <span className="badge bg-secondary">#{idx + 1}</span>
                                    <h6 className="fw-bold mb-0">{consent.title}</h6>
                                    <span className={`badge ${consent.isMandatory ? 'bg-danger' : 'bg-info text-dark'} small`}>
                                      {consent.isMandatory ? 'Obbligatorio' : 'Facoltativo'}
                                    </span>
                                    <span className="badge bg-light text-dark border small">
                                      v{consent.version || 1}
                                    </span>
                                  </div>
                                  <div
                                    className="small text-muted mb-0"
                                    style={{ maxHeight: '60px', overflowY: 'auto' }}
                                    dangerouslySetInnerHTML={{ __html: consent.description }}
                                  />
                                </div>
                              <div className="flex-shrink-0 d-flex flex-column align-items-md-end justify-content-center mt-2 mt-md-0" style={{ minWidth: '180px' }}>
                                <span className={`badge py-2 px-3 text-nowrap ${consent.isAccepted ? 'bg-success' : consent.isOutdated ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                  {consent.isAccepted ? (
                                    <>
                                      <i className="fas fa-check-circle me-1"></i>
                                      Accettato {consent.userConsentVersion ? `(v${consent.userConsentVersion})` : ''}
                                    </>
                                  ) : consent.isOutdated ? (
                                    <>
                                      <i className="fas fa-exclamation-triangle me-1"></i>
                                      Obsoleto (v{consent.userConsentVersion})
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-times-circle me-1"></i>
                                      Rifiutato
                                    </>
                                  )}
                                </span>
                                {consent.acceptedAt && (
                                  <small className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                    {new Date(consent.acceptedAt).toLocaleDateString('it-IT')}
                                  </small>
                                )}
                              </div>
                            </div>
                          ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* 2. Sezioni Informative Personalizzate (Portale Atleta) */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-secondary mb-2">
                        <i className="fas fa-align-left me-2 text-primary"></i>
                        Sezioni Informative Personalizzate (Portale Atleta)
                      </h6>
                      {portalConsentsLoading ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" variant="primary" />
                          <span className="ms-2 small text-muted">Caricamento sezioni informative...</span>
                        </div>
                      ) : (
                        (() => {
                          const userSectionConsents = userData?.portalSectionConsents || [];
                          // Merge configured sections and any historical consents
                          const allSectionItems = [...portalSections];
                          userSectionConsents.forEach(sc => {
                            if (!allSectionItems.some(s => s.id === sc.sectionId || String(s.id).toLowerCase() === String(sc.sectionId).toLowerCase())) {
                              allSectionItems.push({
                                id: sc.sectionId,
                                title: `Sezione (${sc.sectionId})`,
                                version: sc.version,
                                isArchived: true
                              });
                            }
                          });

                          if (allSectionItems.length === 0) {
                            return (
                              <div className="alert alert-light border small text-muted mb-0 py-2">
                                <i className="fas fa-info-circle me-2 text-secondary"></i>
                                Nessuna sezione informativa configurata nel sistema.
                              </div>
                            );
                          }

                          return (
                            <div className="list-group list-group-flush border rounded">
                              {allSectionItems.map((section, idx) => {
                                const consent = userSectionConsents.find(c =>
                                  c.sectionId === section.id ||
                                  String(c.sectionId).toLowerCase() === String(section.id).toLowerCase()
                                );
                                const expectedVersion = (section.version && section.version > 0) ? section.version : 1;
                                const isAccepted = consent != null && consent.version >= expectedVersion;
                                const isOutdated = consent != null && consent.version < expectedVersion;

                                return (
                                  <div key={section.id || idx} className="list-group-item p-3 d-flex justify-content-between align-items-center gap-3">
                                    <div className="flex-grow-1 me-2">
                                      <h6 className="fw-bold mb-1">
                                        {section.title || `Sezione senza titolo (${section.id})`}
                                        <span className="badge bg-light text-secondary ms-2 border">v{expectedVersion}</span>
                                        {section.enabled === false && (
                                          <span className="badge bg-secondary ms-1">Disabilitata</span>
                                        )}
                                      </h6>
                                      <p className="small text-muted mb-0">
                                        {section.content ? (
                                          <span>{section.content.replace(/<[^>]*>/g, '').slice(0, 150)}{section.content.length > 150 ? '...' : ''}</span>
                                        ) : (
                                          <span className="fst-italic">Sezione informativa del portale atleta</span>
                                        )}
                                      </p>
                                    </div>
                                    <div className="flex-shrink-0 d-flex align-items-center justify-content-end" style={{ minWidth: '180px' }}>
                                      {isAccepted ? (
                                        <span className="badge py-2 px-3 bg-success text-nowrap" title={`Accettata versione ${consent.version}`}>
                                          <i className="fas fa-check-circle me-1"></i>
                                          Letto e Confermato {consent.acceptedAt ? `(${new Date(consent.acceptedAt).toLocaleDateString('it-IT')})` : ''}
                                        </span>
                                      ) : isOutdated ? (
                                        <span className="badge py-2 px-3 bg-warning text-dark text-nowrap" title={`Accettata v${consent.version}, versione attuale v${expectedVersion}`}>
                                          <i className="fas fa-exclamation-triangle me-1"></i>
                                          Aggiornamento in attesa (v{consent.version})
                                        </span>
                                      ) : (
                                        <span className="badge py-2 px-3 bg-secondary text-nowrap">
                                          Non visionato
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()
                      )}
                    </div>

                    {/* 3. Documenti e Allegati Ufficiali (Portale Atleta) */}
                    <div>
                      <h6 className="fw-bold text-secondary mb-2">
                        <i className="fas fa-paperclip me-2 text-primary"></i>
                        Documenti e Allegati Ufficiali (Portale Atleta)
                      </h6>
                      {portalConsentsLoading ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" variant="primary" />
                          <span className="ms-2 small text-muted">Caricamento allegati...</span>
                        </div>
                      ) : (
                        (() => {
                          const userAttachmentConsents = userData?.portalAttachmentConsents || [];
                          // Merge configured portal attachments and historical consents
                          const allAttachmentItems = [...portalAttachments];
                          userAttachmentConsents.forEach(ac => {
                            if (!allAttachmentItems.some(a => a.id === ac.attachmentId || String(a.id).toLowerCase() === String(ac.attachmentId).toLowerCase())) {
                              if (ac.attachment) {
                                allAttachmentItems.push({
                                  ...ac.attachment,
                                  isArchived: true
                                });
                              } else {
                                allAttachmentItems.push({
                                  id: ac.attachmentId,
                                  title: `Allegato archiviato (${ac.attachmentId})`,
                                  fileVersion: ac.fileVersion,
                                  isArchived: true
                                });
                              }
                            }
                          });

                          if (allAttachmentItems.length === 0) {
                            return (
                              <div className="alert alert-light border small text-muted mb-0 py-2">
                                <i className="fas fa-info-circle me-2 text-secondary"></i>
                                Nessun documento o allegato configurato nel sistema.
                              </div>
                            );
                          }

                          return (
                            <div className="list-group list-group-flush border rounded">
                              {allAttachmentItems.map((attachment, idx) => {
                                const consent = userAttachmentConsents.find(c =>
                                  c.attachmentId === attachment.id ||
                                  String(c.attachmentId).toLowerCase() === String(attachment.id).toLowerCase()
                                );
                                const expectedVersion = attachment.fileVersion || 1;
                                const isAccepted = consent != null && consent.fileVersion >= expectedVersion;
                                const isOutdated = consent != null && consent.fileVersion < expectedVersion;

                                return (
                                  <div key={attachment.id || idx} className="list-group-item p-3 d-flex justify-content-between align-items-center gap-3 flex-wrap">
                                    <div className="d-flex align-items-start gap-3 flex-grow-1 me-2">
                                      <div className="fs-3 text-primary mt-1">
                                        <i className={getAttachmentFileIcon(attachment.fileName)}></i>
                                      </div>
                                      <div>
                                        <h6 className="fw-bold mb-1">
                                          {attachment.title || attachment.fileName || 'Allegato'}
                                          <span className="badge bg-light text-secondary ms-2 border">v{expectedVersion}</span>
                                          {attachment.isEnabled === false && (
                                            <span className="badge bg-secondary ms-1">Disabilitato</span>
                                          )}
                                        </h6>
                                        {attachment.description && (
                                          <p className="small text-muted mb-1">{attachment.description}</p>
                                        )}
                                        <div className="small text-muted">
                                          {attachment.fileName && <span>File: <strong>{attachment.fileName}</strong></span>}
                                          {attachment.fileSize && <span className="ms-2">({formatAttachmentSize(attachment.fileSize)})</span>}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex-shrink-0 d-flex align-items-center gap-2">
                                      {attachment.fileName && (
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          onClick={() => handleDownloadAttachmentFile(attachment)}
                                          disabled={downloadingAttachmentId === attachment.id}
                                          title="Scarica allegato"
                                        >
                                          {downloadingAttachmentId === attachment.id ? (
                                            <Spinner animation="border" size="sm" />
                                          ) : (
                                            <i className="fas fa-download me-1"></i>
                                          )}
                                          Scarica
                                        </Button>
                                      )}
                                      <div style={{ minWidth: '180px' }} className="text-end">
                                        {isAccepted ? (
                                          <span className="badge py-2 px-3 bg-success text-nowrap" title={`Accettata versione ${consent.fileVersion}`}>
                                            <i className="fas fa-check-circle me-1"></i>
                                            Accettato {consent.acceptedAt ? `(${new Date(consent.acceptedAt).toLocaleDateString('it-IT')})` : ''}
                                          </span>
                                        ) : isOutdated ? (
                                          <span className="badge py-2 px-3 bg-warning text-dark text-nowrap" title={`Accettata v${consent.fileVersion}, versione attuale v${expectedVersion}`}>
                                            <i className="fas fa-exclamation-triangle me-1"></i>
                                            Aggiornamento in attesa (v{consent.fileVersion})
                                          </span>
                                        ) : (
                                          <span className="badge py-2 px-3 bg-secondary text-nowrap">
                                            Non accettato
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                )}
              </Tab>
            )}

            {/* Tab Accesso Portale - Solo in modalità modifica (solo admin) */}
            {isEditMode && userData && isCurrentUserAdmin && (
              <Tab eventKey="portal-access" title={
                <span>
                  <i className="fas fa-key me-2"></i>
                  Accesso Portale
                </span>
              }>
                {activeTab === 'portal-access' && (
                  <UserPortalAccess
                    userId={userData.id}
                    user={userData}
                    onAccessChanged={loadUser}
                  />
                )}
              </Tab>
            )}
          </Tabs>
        </Card.Body>
      </Card>
      {/* Email Modal */}
      {isEditMode && userData && (
        <SendBroadcastEmailModal
          show={showEmailModal}
          onHide={() => setShowEmailModal(false)}
          selectedUserIds={[userData.id]}
          selectedUsers={[userData]}
          onSuccess={(msg) => { setSuccess(msg); setShowEmailModal(false); }}
          onError={(msg) => setError(msg)}
        />
      )}
      {/* Access Link Modal */}
      {isEditMode && userData && isCurrentUserAdmin && (
        <SendAccessLinkModal
          show={showAccessLinkModal}
          onHide={() => setShowAccessLinkModal(false)}
          user={userData}
          onSuccess={(msg) => { setSuccess(msg); setShowAccessLinkModal(false); loadUser(); }}
          onError={(msg) => setError(msg)}
        />
      )}
      {/* Email Warning Modal */}
      <EmailWarningModal
        show={showEmailWarningModal}
        warnings={emailWarnings}
        onCorrect={() => setShowEmailWarningModal(false)}
        onIgnoreAndProceed={() => {
          setShowEmailWarningModal(false);
          handleSubmit(null, true);
        }}
      />
    </div>
  );
};

export default UserForm;
